import { RECALL_VERDICT_META } from '@/app/recalls/recallStatus';
import { typeDisagreementLabel } from '@/app/recalls/recallTags';
import { Badge } from '@/components/ui/badge';
import { equipmentRecallVerdictsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import { describeMatchedRule, describeModelRule } from '@rock/recalls';
import type { EquipmentId, EquipmentRecallVerdict, HomeId, RecallEvidence } from '@rock/types';
import { type FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

/**
 * Every recall verdict written for one unit, with the evidence behind it — including withdrawn ones,
 * because this is the admin's view of what the matcher decided, not the homeowner's.
 * Renders nothing for the common unit with no verdicts at all.
 */
export const EquipmentRecallsSection: FC<{ homeId: HomeId; equipmentId: EquipmentId }> = ({ homeId, equipmentId }) => {
    const verdicts = useAsyncAtomValue(
        useMemo(() => equipmentRecallVerdictsState({ homeId, equipmentId }), [homeId, equipmentId]),
        [],
    );
    if (verdicts.length === 0) return null;
    return (
        <section className="flex flex-col gap-2 border-t pt-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recalls</h4>
            {verdicts.map((verdict) => (
                <VerdictRow key={verdict.recallId} verdict={verdict} />
            ))}
        </section>
    );
};

const VerdictRow: FC<{ verdict: EquipmentRecallVerdict }> = ({ verdict }) => {
    const meta = RECALL_VERDICT_META[verdict.verdict];
    const withdrawn = verdict.withdrawn;
    return (
        <div className={`flex flex-col gap-1 text-xs sm:text-sm ${withdrawn ? 'text-muted-foreground' : ''}`}>
            <div className="flex flex-wrap items-center gap-2">
                <Link
                    to={`/products/recalls/${encodeURIComponent(verdict.recallId)}`}
                    className="min-w-0 truncate font-medium hover:text-primary hover:underline"
                >
                    {verdict.display.title}
                </Link>
                {withdrawn ? (
                    <Badge variant="outline" className="text-muted-foreground" title={formatRelative(withdrawn.at)}>
                        withdrawn ({withdrawn.reason})
                    </Badge>
                ) : (
                    <Badge variant={meta.variant} className={meta.className}>
                        {meta.label}
                    </Badge>
                )}
                {verdict.needs.length > 0 && !withdrawn && (
                    <span className="text-muted-foreground">needs {verdict.needs.join(', ')}</span>
                )}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span className="font-mono">{describeEvidence(verdict.evidence)}</span>
                {verdict.notifiedAt && (
                    <span>
                        notified {formatRelative(verdict.notifiedAt)}
                        {verdict.notifiedVerdict && verdict.notifiedVerdict !== verdict.verdict
                            ? ` as ${verdict.notifiedVerdict}`
                            : ''}
                    </span>
                )}
                {verdict.resolution && (
                    <span>
                        homeowner: {verdict.resolution.outcome} {formatRelative(verdict.resolution.at)}
                        {verdict.resolution.note ? ` — ${verdict.resolution.note}` : ''}
                    </span>
                )}
                {verdict.typeConfirmedAt && <span>type confirmed {formatRelative(verdict.typeConfirmedAt)}</span>}
                {verdict.acceptedAt && <span>homeowner: applies {formatRelative(verdict.acceptedAt)}</span>}
            </div>
        </div>
    );
};

/** One line: which rule hit, how strong, and the serial rule that named the unit, if one did. */
function describeEvidence(evidence: RecallEvidence): string {
    const parts = [
        describeMatchedRule(evidence),
        evidence.strength,
        typeDisagreementLabel(evidence),
        evidence.serialRule ? `serial ${describeModelRule(evidence.serialRule)}` : undefined,
    ];
    return parts.filter((part) => !!part).join(' · ');
}
